import { useState, useMemo, useCallback } from 'react'

import {
  faUpRightFromSquare,
  faChevronDown,
  faChevronUp,
  faPlus,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  Banner,
  Button,
  CurrencyStringCents,
  FormattedMessage,
  LinkAsButton,
  Radio,
  Spinner,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import {
  type PayMethod,
  PayMethodStatusEnum,
  PayMethodTypeEnum,
} from '@b-stock/payments-methods-api-client'

import { ActionLink } from '@/buyer-portal/components/ActionLink'
import {
  getSortedPayEntities,
  isBuyerDenied as isBuyerDeniedNetTerms,
  isDirectPaymentMethod,
} from '@/buyer-portal/components/features/order/checkout/helpers/utils'
import PaymentMethodCard from '@/buyer-portal/components/features/order/checkout/PaymentMethods/PaymentMethodCard'
import { PaymentMethodTag } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/PaymentMethodTag'
import {
  BalancePortalLink,
  InformativeBanner,
} from '@/buyer-portal/components/features/order/checkout/PaymentMethods/PayWithOtherOptions/InformativeBanner'
import {
  isAchMethod,
  getDetails,
  getOwner,
  getPaymentRestrictionReason,
  isPaymentMethodDisabled,
} from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import type { NetTermsPayMethod } from '@/buyer-portal/components/features/order/checkout/shared'
import { useListingPaymentRestrictions } from '@/buyer-portal/helpers/useListingPaymentRestrictions'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import type { AutoPaymentMethodListProps } from './types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const CardsSection = styled.div`
  margin-bottom: 1rem;
`

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 0.125rem;
  margin-bottom: 1rem;
`

const PaymentDivider = styled.div<{ $autoPayEnforced?: boolean }>`
  display: flex;
  align-items: center;
  margin: ${({ $autoPayEnforced }) =>
    $autoPayEnforced ? '0.75rem 0' : '1.5rem 0'};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 0.0625rem;
    background-color: ${Colors.Semantic.Neutral.medium_grey};
  }

  &::before {
    margin-right: 1rem;
  }

  &::after {
    margin-left: 1rem;
  }
`

const PaymentDividerText = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  white-space: nowrap;
  text-transform: uppercase;
`

const OptionContainer = styled.div<{
  $isSelected?: boolean
  $disabled?: boolean
  $autoPayEnforced?: boolean
}>`
  padding: 1rem;
  border: 0.0625rem solid
    ${({ $isSelected, $disabled, $autoPayEnforced }) =>
      $autoPayEnforced && $disabled
        ? 'transparent'
        : $isSelected
          ? Colors.Semantic.BStock.onhover
          : Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  cursor: ${({ $disabled, $isSelected }) =>
    $disabled || $isSelected ? 'default' : 'pointer'};
  transition: border-color 0.2s ease;
  background-color: ${({ $disabled }) =>
    $disabled ? Colors.Semantic.Neutral.light_grey : 'transparent'};

  ${({ $autoPayEnforced }) =>
    $autoPayEnforced
      ? `
    display: flex;
    flex-direction: column;
  `
      : undefined}

  margin-bottom: 0.75rem;

  &:hover {
    border-color: ${({ $disabled }) =>
      $disabled
        ? Colors.Semantic.Neutral.medium_grey
        : Colors.Semantic.BStock.default.lightBackground};
  }

  ${({ $autoPayEnforced, $disabled }) =>
    $autoPayEnforced && $disabled
      ? `
    background-color: ${Colors.Semantic.Neutral.light_grey};
    pointer-events: none;
  `
      : undefined}
`

const NetTermsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const NetTermsContent = styled.div`
  flex: 1;
`

const NetTermsTitle = styled.div`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const NetTermsDescription = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin-top: 0.25rem;
`

const SavedMethodsTitle = styled.div`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 1rem;
`

const NetTermsExpandedSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
`

const ApplyNetTermsContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`

const CreditInfoGrid = styled.ul`
  display: flex;
  flex-direction: row;
  padding: 0;
  justify-content: space-around;
  list-style: none;
  align-items: center;
  margin: 0 0 1rem 0;
`

const CreditInfoItem = styled.li`
  text-align: center;
`

const CreditLabel = styled.strong`
  ${Typography.Body4}
  display: block;
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin-bottom: 0.25rem;
`

const CreditValue = styled.span`
  ${Typography.Body3}
  display: block;
  color: ${Colors.Semantic.Neutral.black};
`

const InfoText = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  text-align: center;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 3rem;
  color: ${Colors.Semantic.Neutral.medium_grey};
`

const ExpandedText = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  text-align: center;
  max-width: 32rem;
`

const StyledBanner = styled(Banner)`
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
`

const Subtitle = styled.div`
  ${Typography.Subtitle1}
  margin-bottom: 1rem;
`

const ns = 'BidConfirmationModal.autoPay.modal'

export const AutoPaymentMethodList = ({
  paymentMethods,
  selectedPaymentMethodId,
  onSelect,
  onAddNew,
  onApplyForNetTerms,
  isStartingQualification,
  payableAmountInCents,
  isLoading = false,
  onCreditCardSelected,
  listingIsCreditCardEnabled,
  onWireSelect,
  isWireSelected = false,
  currency,
}: AutoPaymentMethodListProps) => {
  const { trackButtonClicked } = useAnalytics()
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)
  const isBalanceCCFeatureEnabled = useFlag<boolean>(
    FeatureFlags.ENABLE_BALANCE_CREDIT_CARDS
  )

  // FR-016: non-USD listings (e.g. CAD) only support Wire Transfer — digital
  // methods (ACH/CC/Net Terms) must be blocked, matching the compatibility
  // rule already enforced at checkout (the wire-only branches in Checkout.tsx).
  const isNonUsdCurrency = !!currency && currency.toUpperCase() !== 'USD'

  // Payment restrictions — account-level merged with listing-level constraints
  const payRestrictions = useListingPaymentRestrictions(
    listingIsCreditCardEnabled
  )
  // The Seller Collects fee picker never renders this list: it has its own
  // card-only list (SellerCollectsFeeCardList).
  const isACHBlocked = isNonUsdCurrency || payRestrictions.isACHBlocked
  // Only takes effect when isBalanceCCFeatureEnabled=true; without it, CC methods are excluded
  // from regularMethods so this value is never read.
  const isCreditCardBlocked =
    isNonUsdCurrency ||
    (payRestrictions.isCreditCardBlocked &&
      (autoPayEnforced || isBalanceCCFeatureEnabled))
  const isNetTermsBlocked =
    isNonUsdCurrency || (autoPayEnforced && payRestrictions.isNetTermsBlocked)

  // CC cards render only when isBalanceCCFeatureEnabled, where isCreditCardBlocked
  // equals the merged value — so the split CC booleans can be passed directly.
  // Account-blocked ACH uses the same unavailable treatment in both picker layouts.
  // BUGS-5487: a non-USD listing disables ACH/CC for a currency/listing reason,
  // not an account-level block — gray out only, matching FP-2418's CC-disabled
  // treatment, instead of the "Restricted" badge account-level blocks get.
  const restrictionReasonFor = (type: PayMethod['type']) =>
    isNonUsdCurrency
      ? 'listing_cc_disabled'
      : isAchMethod(type) && payRestrictions.isACHBlocked
        ? 'ach_unavailable'
        : getPaymentRestrictionReason(type, {
            isACHBlocked,
            isCreditCardBlockedByAccount:
              payRestrictions.isCreditCardBlockedByAccount,
            isCreditCardDisabledByListing:
              payRestrictions.isCreditCardDisabledByListing,
          })

  // FF OFF (legacy): keep the initially selected method first when sorting the list.
  const [legacyDefaultId] = useState(selectedPaymentMethodId)

  const regularMethods = useMemo(() => {
    const filtered = paymentMethods.filter((method) =>
      isDirectPaymentMethod(method.type, isBalanceCCFeatureEnabled)
    )

    // FF ON: use checkout's getSortedPayEntities — restricted to end, primary first
    if (autoPayEnforced) {
      return getSortedPayEntities(filtered, payRestrictions)
    }

    // FF OFF (legacy): sort by initially selected method — unchanged
    return filtered.sort((a, b) => {
      if (a.id === legacyDefaultId) return -1
      if (b.id === legacyDefaultId) return 1
      return 0
    })
  }, [
    paymentMethods,
    legacyDefaultId,
    autoPayEnforced,
    payRestrictions,
    isBalanceCCFeatureEnabled,
  ])

  const netTermsMethod = useMemo(
    () =>
      paymentMethods.find(
        (method) => method.type === PayMethodTypeEnum.Getbalance
      ),
    [paymentMethods]
  )

  const [isShowingMore, setIsShowingMore] = useState(false)
  const [showCcBanner, setShowCcBanner] = useState(true)

  const canAddBalanceCC =
    autoPayEnforced && isBalanceCCFeatureEnabled && !isCreditCardBlocked

  const hasOnlyBankMethods = useMemo(() => {
    if (regularMethods.length === 0) return false
    return regularMethods.every(
      (method) =>
        method.type === PayMethodTypeEnum.Bank ||
        method.type === PayMethodTypeEnum.StripeFinConn
    )
  }, [regularMethods])

  // FF-on only: Reorder methods to show selected first (matching checkout pattern)
  const reorderedMethods = useMemo(() => {
    if (regularMethods.length <= 1) return regularMethods

    if (
      autoPayEnforced &&
      selectedPaymentMethodId &&
      regularMethods[0]?.id !== selectedPaymentMethodId
    ) {
      const selectedMethod = regularMethods.find(
        (m) => m.id === selectedPaymentMethodId
      )
      if (selectedMethod) {
        const otherMethods = regularMethods.filter(
          (m) => m.id !== selectedPaymentMethodId
        )
        return [selectedMethod, ...otherMethods]
      }
    }

    return regularMethods
  }, [regularMethods, selectedPaymentMethodId, autoPayEnforced])

  const availableMethods = useMemo(
    () =>
      reorderedMethods.filter(
        (method) =>
          !isPaymentMethodDisabled(method.type, {
            ach: isACHBlocked,
            card: isCreditCardBlocked,
          })
      ),
    [reorderedMethods, isACHBlocked, isCreditCardBlocked]
  )
  const collapseUnavailable = payRestrictions.isACHBlocked
  const methodsToShow = useMemo(() => {
    // Collapsed: show reordered (selected first), Expanded: show natural order (default first)
    if (collapseUnavailable) {
      return isShowingMore
        ? [
            ...availableMethods,
            ...regularMethods.filter(
              (method) =>
                !availableMethods.some(
                  (available) => available.id === method.id
                )
            ),
          ]
        : availableMethods
    }
    return isShowingMore ? regularMethods : reorderedMethods.slice(0, 1)
  }, [
    isShowingMore,
    regularMethods,
    reorderedMethods,
    collapseUnavailable,
    availableMethods,
  ])

  const netTermsState = useMemo(() => {
    if (!netTermsMethod) {
      return {
        creditInfo: null,
        qualification: null,
        isBuyerDenied: false,
        isCapabilityDisabled: false,
        isPending: false,
        isPendingActivation: false,
        isNetTermsEnabled: false,
        hasNoAvailableCredit: false,
        insufficientCredit: false,
      }
    }

    const typedNetTerms = netTermsMethod as NetTermsPayMethod
    const creditInfo = typedNetTerms.details.creditInfo
    // getbalance is typed as always-present, but a malformed payments-methods-svc
    // response can omit it at runtime.
    // oxlint-disable-next-line typescript-eslint/no-unnecessary-condition
    const qualification = typedNetTerms.getbalance?.qualification
    const qualificationStatus = qualification?.status
    const creditStatus = creditInfo?.status
    const payMethodStatus = netTermsMethod.status

    const availableNetTermsCredit = creditInfo?.availableCreditInCents ?? 0

    const isCapabilityDisabled =
      isNetTermsBlocked ||
      (payMethodStatus === PayMethodStatusEnum.Canceled &&
        creditStatus !== 'suspended' &&
        qualificationStatus !== 'declined')

    const hasNoAvailableCredit = availableNetTermsCredit <= 0

    const insufficientCredit =
      autoPayEnforced &&
      payableAmountInCents !== undefined &&
      payableAmountInCents > 0 &&
      availableNetTermsCredit < payableAmountInCents

    return {
      creditInfo,
      qualification,
      isBuyerDenied: isBuyerDeniedNetTerms(netTermsMethod),
      isCapabilityDisabled,
      isPending: qualificationStatus === 'pending',
      isPendingActivation: qualificationStatus === 'pendingActivation',
      isNetTermsEnabled:
        qualificationStatus === 'approved' && !isNetTermsBlocked,
      hasNoAvailableCredit,
      insufficientCredit,
    }
  }, [netTermsMethod, isNetTermsBlocked, autoPayEnforced, payableAmountInCents])

  const {
    creditInfo,
    qualification,
    isBuyerDenied,
    isCapabilityDisabled,
    isPending,
    isPendingActivation,
    isNetTermsEnabled,
    hasNoAvailableCredit,
    insufficientCredit,
  } = netTermsState

  const qualificationStatus = qualification?.status

  const handleCardClick = useCallback(
    (methodId: string) => {
      const method = regularMethods.find((m) => m.id === methodId)
      if (
        method &&
        isPaymentMethodDisabled(method.type, {
          ach: isACHBlocked,
          card: isCreditCardBlocked,
        })
      ) {
        return
      }
      onSelect(methodId)
    },
    [onSelect, regularMethods, isACHBlocked, isCreditCardBlocked]
  )

  const handleNetTermsClick = useCallback(() => {
    if (isNetTermsEnabled && netTermsMethod) {
      onSelect(netTermsMethod.id)
    }
  }, [isNetTermsEnabled, netTermsMethod, onSelect])

  // Legacy (FF off): full-component loading spinner
  if (isLoading && !autoPayEnforced) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    )
  }

  // Bid flow: no split payment, so any credit issue fully disables net terms
  const showDisabledPayWithTermsOption =
    hasNoAvailableCredit ||
    !netTermsMethod ||
    isBuyerDenied ||
    isCapabilityDisabled ||
    insufficientCredit ||
    isPendingActivation ||
    isPending

  // FF-off (legacy): restore original v47.1.3 simple gate — anything not 'approved' is disabled
  // FF-on: use expanded check with payment restrictions
  const isNetTermsOptionDisabled = autoPayEnforced
    ? showDisabledPayWithTermsOption
    : !isNetTermsEnabled

  const hasUnavailableSavedNetTerms =
    collapseUnavailable && !!netTermsMethod && isNetTermsOptionDisabled
  const hiddenMethodCount =
    regularMethods.length -
    (collapseUnavailable ? availableMethods.length : 1) +
    (hasUnavailableSavedNetTerms ? 1 : 0)
  const hasMoreMethods = hiddenMethodCount > 0

  const isNetTermsSelected =
    isNetTermsEnabled &&
    !isNetTermsOptionDisabled &&
    netTermsMethod?.id === selectedPaymentMethodId

  const canAddPaymentMethod = !isACHBlocked || !isCreditCardBlocked

  const renderNetTermsExpandedContent = () => {
    // FF-off: simple check — FF-on: also excludes isBuyerDenied
    if (
      netTermsMethod &&
      creditInfo &&
      isNetTermsEnabled &&
      (!autoPayEnforced || !isBuyerDenied)
    ) {
      return (
        <>
          <CreditInfoGrid>
            <CreditInfoItem>
              <CreditLabel>
                <FormattedMessage
                  id={`${ns}.payWithTerms.creditInfo.creditLimit`}
                />
              </CreditLabel>
              <CreditValue>
                <CurrencyStringCents
                  value={creditInfo.creditLimitInCents ?? 0}
                  currency={qualification?.currency ?? 'USD'}
                />
              </CreditValue>
            </CreditInfoItem>
            <CreditInfoItem>
              <CreditLabel>
                <FormattedMessage
                  id={`${ns}.payWithTerms.creditInfo.currentBalance`}
                />
              </CreditLabel>
              <CreditValue>
                <CurrencyStringCents
                  value={creditInfo.outstandingCreditInCents ?? 0}
                  currency={qualification?.currency ?? 'USD'}
                />
              </CreditValue>
            </CreditInfoItem>
            <CreditInfoItem>
              <CreditLabel>
                <FormattedMessage
                  id={`${ns}.payWithTerms.creditInfo.availableCredit`}
                />
              </CreditLabel>
              <CreditValue>
                <CurrencyStringCents
                  value={creditInfo.availableCreditInCents ?? 0}
                  currency={qualification?.currency ?? 'USD'}
                />
              </CreditValue>
            </CreditInfoItem>
          </CreditInfoGrid>

          {autoPayEnforced && hasNoAvailableCredit ? (
            <InformativeBanner
              statusText="noAvailableCredit"
              balanceMethod={netTermsMethod as NetTermsPayMethod}
            />
          ) : autoPayEnforced && insufficientCredit ? (
            <InformativeBanner
              statusText="insufficientCredit"
              balanceMethod={netTermsMethod as NetTermsPayMethod}
            />
          ) : (
            <Banner type="info" hideIcon>
              <InfoText>
                <FormattedMessage
                  id={`${ns}.payWithTerms.instructions.approved`}
                  values={{
                    link: (chunks: React.ReactNode) => (
                      <BalancePortalLink
                        chunks={chunks}
                        balanceMethod={netTermsMethod as NetTermsPayMethod}
                      />
                    ),
                  }}
                />
              </InfoText>
            </Banner>
          )}
        </>
      )
    }

    // Currency-based block takes precedence over account-status reasons —
    // the buyer's B-Stock account can be fully enabled for Net Terms even
    // though this listing's currency isn't supported.
    if (isNonUsdCurrency) {
      return (
        <InformativeBanner
          statusText="currencyNotSupported"
          balanceMethod={netTermsMethod as NetTermsPayMethod}
        />
      )
    }

    if (isBuyerDenied) {
      const bannerStatus =
        qualificationStatus === 'declined'
          ? 'qualificationDenied'
          : 'accountSuspended'
      const bannerEventName =
        qualificationStatus === 'declined'
          ? 're_apply_balance_autocharge'
          : 'account_suspended_click_autocharge'
      return (
        <InformativeBanner
          statusText={bannerStatus}
          balanceMethod={netTermsMethod as NetTermsPayMethod}
          onLinkClick={() =>
            trackButtonClicked({ buttonName: bannerEventName })
          }
        />
      )
    }

    // Legacy: isCapabilityDisabled BEFORE isPending/isPendingActivation
    if (!autoPayEnforced && isCapabilityDisabled) {
      return (
        <InformativeBanner
          statusText="bstockDisabled"
          balanceMethod={netTermsMethod as NetTermsPayMethod}
        />
      )
    }

    if (isPending) {
      return (
        <InformativeBanner
          statusText="pending"
          balanceMethod={netTermsMethod as NetTermsPayMethod}
        />
      )
    }

    if (isPendingActivation) {
      return (
        <Banner type="warning" hideIcon>
          <InfoText>
            <FormattedMessage
              id={`${ns}.payWithTerms.instructions.pendingActivation`}
              values={{
                link: (chunks: React.ReactNode) => (
                  <ActionLink
                    href={qualification?.link}
                    onClick={(e) => {
                      e.stopPropagation()
                      trackButtonClicked({
                        buttonName: 'activate_balance_autocharge',
                      })
                    }}
                  >
                    {chunks}
                  </ActionLink>
                ),
                creditLimit: (
                  <CurrencyStringCents
                    value={qualification?.creditLimit ?? 0}
                    currency={qualification?.currency ?? 'USD'}
                  />
                ),
              }}
            />
          </InfoText>
        </Banner>
      )
    }

    // FF-on order: isCapabilityDisabled AFTER isPending/isPendingActivation
    if (autoPayEnforced && isCapabilityDisabled) {
      return (
        <InformativeBanner
          statusText="bstockDisabled"
          balanceMethod={netTermsMethod as NetTermsPayMethod}
        />
      )
    }

    return (
      <ApplyNetTermsContent>
        <IconContainer>
          <FontAwesomeIcon icon={faUpRightFromSquare} size="3x" />
        </IconContainer>
        <ExpandedText>
          <FormattedMessage
            id={`${ns}.payWithTerms.apply`}
            values={{
              link: (chunks: React.ReactNode) => (
                <ActionLink
                  onClick={() => {
                    trackButtonClicked({
                      buttonName: 'apply_through_balance_autocharge',
                    })
                    onApplyForNetTerms()
                  }}
                >
                  {isStartingQualification ? <Spinner size="12px" /> : chunks}
                </ActionLink>
              ),
            }}
          />
        </ExpandedText>
      </ApplyNetTermsContent>
    )
  }

  const netTermsOption = (
    <OptionContainer
      $isSelected={isNetTermsSelected}
      $disabled={isNetTermsOptionDisabled}
      $autoPayEnforced={autoPayEnforced}
      onClick={!isNetTermsOptionDisabled ? handleNetTermsClick : undefined}
    >
      <NetTermsHeader>
        <RadioContainer>
          <Radio
            size={14}
            name="paymentMethod"
            value={netTermsMethod?.id || 'net-terms-disabled'}
            checked={isNetTermsSelected}
            onChange={
              !isNetTermsOptionDisabled ? handleNetTermsClick : undefined
            }
            disabled={isNetTermsOptionDisabled}
          />
        </RadioContainer>
        <NetTermsContent>
          <NetTermsTitle>
            <FormattedMessage id={`${ns}.payWithTerms.title`} />
            {!!netTermsMethod?.primaryAt && (
              <PaymentMethodTag variant="default" />
            )}
            {isNetTermsSelected && <PaymentMethodTag variant="selected" />}
          </NetTermsTitle>
          <NetTermsDescription>
            <FormattedMessage id={`${ns}.payWithTerms.description`} />
          </NetTermsDescription>
        </NetTermsContent>
      </NetTermsHeader>

      {(!autoPayEnforced ||
        !isNetTermsEnabled ||
        isNetTermsSelected ||
        isNetTermsOptionDisabled) && (
        <NetTermsExpandedSection>
          {renderNetTermsExpandedContent()}
        </NetTermsExpandedSection>
      )}
    </OptionContainer>
  )

  return (
    <Container data-testid="auto-payment-method-list">
      {canAddBalanceCC && hasOnlyBankMethods && showCcBanner && (
        <StyledBanner type="info" onDismiss={() => setShowCcBanner(false)}>
          <Subtitle>
            <FormattedMessage id="CheckoutModal.ccAvailable.title" />
          </Subtitle>
          <FormattedMessage
            id="CheckoutModal.ccAvailable.description"
            values={{
              addCcLink: (
                <LinkAsButton
                  onClick={() => {
                    trackButtonClicked({
                      buttonName: 'add_new_cc_initiation_banner',
                      payment_mode: 'CREDIT_CARD',
                    })
                    onCreditCardSelected?.()
                  }}
                >
                  <FormattedMessage id="CheckoutModal.addCcLinkText" />
                </LinkAsButton>
              ),
            }}
          />
        </StyledBanner>
      )}

      <SavedMethodsTitle>
        <FormattedMessage id={`${ns}.savedMethods`} />
      </SavedMethodsTitle>

      {collapseUnavailable && !isNetTermsOptionDisabled && netTermsOption}

      {isLoading && autoPayEnforced ? (
        <CardsSection>
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        </CardsSection>
      ) : (
        regularMethods.length > 0 && (
          <CardsSection>
            {methodsToShow.map((method) => (
              <PaymentMethodCard
                key={method.id}
                name={method.id}
                type={method.type}
                isDefault={!!method.primaryAt}
                // Same predicate drives two distinct visuals: `selected` = the
                // card border/highlight, `isSelected` = the "Selected" badge.
                isSelected={
                  selectedPaymentMethodId === method.id &&
                  !isPaymentMethodDisabled(method.type, {
                    ach: isACHBlocked,
                    card: isCreditCardBlocked,
                  })
                }
                selected={
                  selectedPaymentMethodId === method.id &&
                  !isPaymentMethodDisabled(method.type, {
                    ach: isACHBlocked,
                    card: isCreditCardBlocked,
                  })
                }
                onClick={handleCardClick}
                owner={getOwner(method)}
                details={getDetails(method)}
                value={0}
                totalAmountRemaining={0}
                skipAmountValidation
                disableInput={isPaymentMethodDisabled(method.type, {
                  ach: isACHBlocked,
                  card: isCreditCardBlocked,
                })}
                restrictionReason={restrictionReasonFor(method.type)}
              />
            ))}
          </CardsSection>
        )
      )}

      <ButtonContainer>
        {canAddPaymentMethod && (
          <Button
            appearance="plainTertiary"
            type="button"
            size="small"
            onClick={() => {
              trackButtonClicked({
                buttonName: 'add_new_payment_autocharge',
              })
              onAddNew()
            }}
          >
            <FontAwesomeIcon icon={faPlus} size="sm" />
            <FormattedMessage id={`${ns}.add.paymentMethod.addNew`} />
          </Button>
        )}
        {hasMoreMethods &&
          (isShowingMore ? (
            <Button
              appearance="plainTertiary"
              type="button"
              size="small"
              onClick={() => setIsShowingMore(false)}
            >
              <FormattedMessage id={`${ns}.showLessMethods`} />
              <FontAwesomeIcon icon={faChevronUp} size="sm" />
            </Button>
          ) : (
            <Button
              appearance="plainTertiary"
              type="button"
              size="small"
              onClick={() => {
                trackButtonClicked({
                  buttonName: 'view_more_payment_autocharge',
                })
                setIsShowingMore(true)
              }}
            >
              <FormattedMessage
                id={`${ns}.showMoreMethods`}
                values={{ count: hiddenMethodCount }}
              />
              <FontAwesomeIcon icon={faChevronDown} size="sm" />
            </Button>
          ))}
      </ButtonContainer>

      <PaymentDivider $autoPayEnforced={autoPayEnforced}>
        <PaymentDividerText>
          <FormattedMessage id={`${ns}.paymentOptions.dividerLabel`} />
        </PaymentDividerText>
      </PaymentDivider>

      {(!collapseUnavailable || isNetTermsOptionDisabled) &&
        (!hasUnavailableSavedNetTerms || isShowingMore) &&
        netTermsOption}

      {autoPayEnforced && onWireSelect && (
        <OptionContainer
          $isSelected={isWireSelected}
          $autoPayEnforced={autoPayEnforced}
          onClick={onWireSelect}
        >
          <NetTermsHeader>
            <RadioContainer>
              <Radio
                size={14}
                name="paymentMethod"
                value="wire"
                checked={isWireSelected}
                onChange={onWireSelect}
              />
            </RadioContainer>
            <NetTermsContent>
              <NetTermsTitle>
                <FormattedMessage id="CheckoutPage.wireTransfer.title" />
                {isWireSelected && <PaymentMethodTag variant="selected" />}
              </NetTermsTitle>
              <NetTermsDescription>
                <FormattedMessage id="CheckoutPage.wireTransfer.description" />
              </NetTermsDescription>
            </NetTermsContent>
          </NetTermsHeader>
        </OptionContainer>
      )}
    </Container>
  )
}
